"use client";

import React, { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import CustomInput from "./CustomInput";
import { driverForgottenPasswordSchema } from "@/lib/utils";
import { Loader2 } from "lucide-react";
import { getDriverForgottenPassword } from "@/lib/actions/driver.actions";

const DriverForgottenPassword = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // 1. Define your form.
  const form = useForm<z.infer<typeof driverForgottenPasswordSchema>>({
    resolver: zodResolver(driverForgottenPasswordSchema),
    defaultValues: {
      email: "",
    },
  });

  // 2. Define a submit handler.
  const onSubmit = async (data: z.infer<typeof driverForgottenPasswordSchema>) => {
    setError("");
    setSuccess("");
    setIsLoading(true);
    try {
      const userData = {
        email: data.email.trim(),
      };
      const response = await getDriverForgottenPassword(userData);
      if (!response) {
        setError(
          "Une erreur de connection s'est produite. Veuillez réessayer !"
        );
      } else if (response.error) {
        setError(response.error);
      } else if (response.data?.message) {
        setSuccess(response.data.message);
      } else {
        console.error("DriverForgottenPassword/onSubmit/no response", response);
        setError(
          "Une erreur de connection s'est produite. Veuillez nous contacter !"
        );
      }
    } catch (err) {
      console.error("DriverForgottenPassword/onSubmit", err);
      setError("Une erreur s'est produite. Veuillez réessayer !");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section className="auth-form">
      <header className="flex flex-col gap-5 md:gap-8">
        <Link
          href="https://www.taxisconnect.com/"
          className="flex cursor-pointer items-center gap-1"
        >
          <Image
            src="/icons/icon@2x.png"
            alt="TaxisConnect Logo"
            width={34}
            height={40}
            className="rounded-md"
          />
          <h1 className="text-26 font-ibm-plex-serif font-bold text-black-1 ml-2">
            TaxisConnect Chauffeur
          </h1>
        </Link>
        <div className="text-black-1 text-2xl font-bold">
          Mot de passe oublié ?
        </div>
      </header>
      <div>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            <CustomInput
              control={form.control}
              name={"email"}
              label={"Votre adresse email"}
              placeholder={"exemple@email.com"}
            />
            {success && (
              <div className="text-green-500 text-sm font-medium">
                {success}
              </div>
            )}
            {error && (
              <div className="text-red-500 text-sm font-medium">{error}</div>
            )}

            <div className="flex flex-col gap-4">
              <Button type="submit" className="form-btn" disabled={isLoading}>
                {isLoading ? (
                  <div className="flex">
                    <Loader2 size={20} className="animate-spin" /> &nbsp;
                    Chargement...
                  </div>
                ) : (
                  "Envoyer mon mot de passe"
                )}
              </Button>
            </div>
          </form>
        </Form>
        <footer className="flex justify-center gap-1 flex-col">
          <div className="text-16 font-normal text-gray-600">
            Pour nous joindre :{" "}
            <a href="mailto:commercial@taxisconnect.com" className="underline">
              commercial@taxisconnect.com
            </a>{" "}
            ou 09 75 120 120 (appel local) aux heures de bureau
          </div>
          <Link href={"/driver-sign-in"}>
            <p className="text-16 font-semibold text-blue-600">Se connecter</p>
          </Link>
        </footer>
      </div>
    </section>
  );
};

export default DriverForgottenPassword;

